import { useState, useRef, useEffect } from "react";
import { useTheme } from "@hooks/useTheme";
import { ChevronDownIcon, ChevronUpIcon } from "../Icons";
import { useTranslation } from "react-i18next";

const CustomTwoColumnSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  nameColumn = "name",
  originalColumn = "original",
  displayName = "displayName",
  size = "md",
  nameHeader = "Component",
  originalHeader = "Original"
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Size configurations
  const sizeConfigs = {
    sm: {
      minHeight: "1.5rem",
      fontSize: "0.75rem",
      padding: "0px 0.5rem",
      borderRadius: "0.25rem",
    },
    md: {
      minHeight: "2.5rem",
      fontSize: "0.875rem",
      padding: "0px 0.75rem",
      borderRadius: "0.5rem",
    },
    lg: {
      minHeight: "3rem",
      fontSize: "1.125rem",
      padding: "0px 1rem",
      borderRadius: "0.5rem",
    },
    xl: {
      minHeight: "3.5rem",
      fontSize: "1.25rem",
      padding: "0px 1.25rem",
      borderRadius: "0.75rem",
    },
  };

  const currentSize = sizeConfigs[size] || sizeConfigs.md;

  // Calculate dropdown position when opening
  const calculateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: 800 // Fixed width for dropdown (longer than selectbox)
      });
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHoveredIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => {
        calculateDropdownPosition();
      };

      const handleResize = () => {
        calculateDropdownPosition();
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  // Handle option selection
  const handleOptionClick = (option) => {
    onChange(option);
    setIsOpen(false);
    setHoveredIndex(-1);
  };

  // Get display value
  const getDisplayValue = () => {
    if (!value) return "";
    
    // If the value has originalFlag === "Y", show "Original [nameHeader]"
    if (value.originalFlag === "Y") {
      return `${t("open-source.original-header")} ${nameHeader}`;
    }
    
    return value[displayName] || value[nameColumn] || "";
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
      }
      setIsOpen(!isOpen);
      if (!isOpen) {
        setHoveredIndex(-1);
      }
    }
  };

  // Handle option hover
  const handleOptionHover = (index) => {
    setHoveredIndex(index);
  };

  // Handle option leave
  const handleOptionLeave = () => {
    setHoveredIndex(-1);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Select Input */}
      <div
        ref={inputRef}
        className={`
          flex items-center justify-between 
          border rounded-lg cursor-pointer
          transition-colors duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        style={{
          minHeight: currentSize.minHeight,
          fontSize: currentSize.fontSize,
          padding: currentSize.padding,
          borderRadius: currentSize.borderRadius,
          backgroundColor: colors.bgInputColor,
          borderColor: isOpen ? colors.borderActiveColor : colors.borderPrimaryColor,
          color: colors.textPlaceholderColor,
        }}
        onClick={toggleDropdown}
        onMouseEnter={() => {
          if (!disabled && !isOpen) {
            // Apply hover effect
          }
        }}
      >
        <span 
          style={{
            color: getDisplayValue() ? colors.textPrimaryColor : colors.textPlaceholderColor,
            margin: 0,
            padding: 0,
            fontSize: currentSize.fontSize,
            textOverflow: "ellipsis",
            overflow: "hidden"
          }}
        >
          {getDisplayValue() || placeholder}
        </span>
        {!disabled && (
          <div style={{ margin: 0, padding: 0 }}>
            {isOpen ? (
              <ChevronUpIcon size={16} style={{ color: colors.textSubTitle }} />
            ) : (
              <ChevronDownIcon size={16} style={{ color: colors.textSubTitle }} />
            )}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 9999,
            height: "15rem",
            backgroundColor: colors.bgPrimaryColor,
            borderRadius: "0.25rem",
            borderTop: "none",
            boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)",
            padding: "0.125rem",
          }}
        >
          {/* Options List */}
          <div
            className="custom-scrollbar"
            style={{
              borderRadius: "0.5rem",
              padding: "0.5rem",
              margin: 0,
              backgroundColor: colors.bgPrimaryColor,
              color: colors.textPrimaryColor,
              maxHeight: "15rem",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: `${colors.borderPrimaryColor} transparent`,
            }}
          >
            {options.length === 0 ? (
              <div 
                style={{
                  padding: "0.5rem 0.75rem",
                  color: colors.textSubTitle,
                  fontSize: currentSize.fontSize,
                }}
              >
                No options found
              </div>
            ) : (
              <>
                {/* Header Row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "0.8fr 0.2fr",
                    gap: "0.5rem",
                    padding: size === "sm" ? "0.375rem 0.625rem" : 
                             size === "lg" ? "0.625rem 1rem" : 
                             size === "xl" ? "0.75rem 1.25rem" : 
                             "0.5rem 0.75rem", // md default
                    borderBottom: `1px solid ${colors.borderPrimaryColor}`,
                    marginBottom: "0.5rem",
                    fontWeight: 600,
                    fontSize: currentSize.fontSize,
                    color: colors.textSubTitle,
                    backgroundColor: colors.bgSecondary || "#f8f9fa",
                    borderRadius: "0.25rem",
                  }}
                >
                  <div>{nameHeader}</div>
                  <div>{originalHeader}</div>
                </div>

                {/* Options */}
                {options.map((option, index) => {
                  const isSelected = value && value[nameColumn] === option[nameColumn];
                  const isHovered = hoveredIndex === index;
                  const isLast = index === options.length - 1;
                  
                  // Determine background color based on state (like CustomSelect)
                  let backgroundColor = colors.bgPrimaryColor;
                  if (isSelected) {
                    backgroundColor = colors.bgSecondary || "#f3f4f6";
                  } else if (isHovered) {
                    backgroundColor = colors.bgSecondary || "#f3f4f6";
                  }
                  
                  return (
                    <div
                      key={index}
                      style={{
                        border: isSelected ? `1px solid ${colors.dropdownBorderColor}` : "none",
                        zIndex: isSelected ? 1000 : 0,
                        backgroundColor: backgroundColor,
                        color: isSelected ? colors.textActiveColor : colors.textPrimaryColor,
                        boxShadow: isSelected ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                        borderRadius: "0.25rem",
                        position: "relative",
                        overflow: "visible",
                        fontSize: currentSize.fontSize,
                        padding: size === "sm" ? "0.375rem 0.625rem" : 
                                 size === "lg" ? "0.625rem 1rem" : 
                                 size === "xl" ? "0.75rem 1.25rem" : 
                                 "0.5rem 0.75rem", // md default
                        cursor: "default",
                        marginBottom: !isLast ? "0.125rem" : 0,
                        transition: "all 0.15s ease-in-out",
                      }}
                      onClick={() => handleOptionClick(option)}
                      onMouseEnter={() => handleOptionHover(index)}
                      onMouseLeave={handleOptionLeave}
                    >
                      <div style={{ 
                       display: "grid", 
                       gridTemplateColumns: "0.8fr 0.2fr", 
                       gap: "0.5rem",
                       wordBreak: "break-word",
                       overflowWrap: "break-word"
                     }}>
                       <div style={{ 
                         fontWeight: 500,
                         wordBreak: "break-word",
                         overflowWrap: "break-word",
                         whiteSpace: "normal"
                       }}>
                         {option[nameColumn] || ''}
                       </div>
                       <div style={{ 
                         fontSize: "0.875em", 
                         opacity: 0.8,
                         wordBreak: "break-word",
                         overflowWrap: "break-word",
                         whiteSpace: "normal"
                       }}>
                         {option[originalColumn] || ''}
                       </div>
                     </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Add custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleId = 'custom-two-column-select-scrollbar';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = scrollbarStyles;
    document.head.appendChild(style);
  }
}

export default CustomTwoColumnSelect;